import type { TenantSettings, NamedColor } from "@/lib/tenant/TenantSettingsProvider";
import type { CustomThemeOptions } from "fansunited-frontend-core";
import { getFontStack } from "@/config/fonts";
import { DEFAULT_NAMED_COLORS, DEFAULT_GLOBALS } from "./defaults";

/**
 * Theme options shape accepted by Fans United widget components.
 * Alias of the library's own CustomThemeOptions so new library versions
 * stay in sync without hand-maintenance.
 */
export type FUWidgetThemeOptions = CustomThemeOptions;

interface RadiusScale {
    none: string;
    "2xs": string;
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    "2xl": string;
    full: string;
}

/**
 * Lighten a hex color by a percentage (for soft backgrounds)
 */
function lightenColor(hex: string, percent: number): string {
    const num = parseInt(hex.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, (num >> 16) + amt);
    const G = Math.min(255, ((num >> 8) & 0x00ff) + amt);
    const B = Math.min(255, (num & 0x0000ff) + amt);
    return `#${((1 << 24) | (R << 16) | (G << 8) | B).toString(16).slice(1)}`;
}

/**
 * Darken a hex color by a percentage (for dark mode soft backgrounds)
 */
function darkenColor(hex: string, percent: number): string {
    const num = parseInt(hex.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max(0, (num >> 16) - amt);
    const G = Math.max(0, ((num >> 8) & 0x00ff) - amt);
    const B = Math.max(0, (num & 0x0000ff) - amt);
    return `#${((1 << 24) | (R << 16) | (G << 8) | B).toString(16).slice(1)}`;
}

/**
 * Convert tenant border radius to the gamification component radius scale
 */
function mapRadiusScale(tenantRadius?: string | null): RadiusScale {
    const baseRadius = tenantRadius || DEFAULT_GLOBALS.radius;

    return {
        none: "0px",
        "2xs": "2px",
        xs: "4px",
        sm: "8px",
        md: baseRadius === "0" ? "0px" : "12px",
        lg: "16px",
        xl: "24px",
        "2xl": "32px",
        full: "1000px",
    };
}

/** Look up a named color by name from the palette, with a fallback. */
function lookupColor(namedColors: NamedColor[], name: string, fallback: string): string {
    const found = namedColors.find((c) => c.name === name);
    return found?.value || fallback;
}

/**
 * Derive light/dark mode from the tenant's `background` named color using the
 * Rec. 709 perceived-luminance approximation. Used by shadow-DOM widgets
 * (EventCard) whose color scheme selection depends on a single `mode` flag.
 */
export function resolveThemeMode(settings?: TenantSettings | null): "light" | "dark" {
    const bg = lookupColor(settings?.namedColors ?? [], "background", "#ffffff");
    const hex = bg.replace("#", "");
    if (hex.length !== 6) return "light";
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? "light" : "dark";
}

/**
 * Maps tenant settings to CustomThemeOptions for FansUnited gamification components.
 */
export function mapTenantToFUWidgetTheme(settings?: TenantSettings | null): FUWidgetThemeOptions {
    const namedColors = settings?.namedColors ?? DEFAULT_NAMED_COLORS.map((c) => ({ name: c.name, value: c.value }));
    const hasBackgroundImage = !!settings?.gamificationDefaultBgUrl;

    // Extract colors from named colors palette
    const primary = lookupColor(namedColors, "primary", "#3b82f6");
    const primaryForeground = lookupColor(namedColors, "primaryForeground", "#ffffff");
    const success = lookupColor(namedColors, "success", "#22c55e");
    const warning = lookupColor(namedColors, "warning", "#f59e0b");
    const error = lookupColor(namedColors, "error", "#ef4444");
    const card = lookupColor(namedColors, "card", "#ffffff");
    const border = lookupColor(namedColors, "border", "#e2e8f0");

    // Font family from global defaults
    const fontFamily = settings?.fontFamily || DEFAULT_GLOBALS.fontFamily;
    const fontFamilyWithFallback = `${fontFamily}, ${getFontStack(fontFamily)}`;

    // Border radius from global defaults
    const radius = settings?.radius || DEFAULT_GLOBALS.radius;

    // The light color scheme is always used by the library for answer option
    // text (hardcoded white button bg + colorSchemes.light.textColor). We must
    // ensure these values are dark regardless of the tenant's own theme mode.
    const lightColorScheme = {
        palette: {
            primary: {
                plainColor: primary,
                outlinedBorder: primary,
                onPrimary: primaryForeground,
                primaryContainer: primary,
            },
            success: {
                plainColor: success,
                outlinedBorder: success,
                softBg: lightenColor(success, 85),
            },
            danger: {
                plainColor: error,
                outlinedBorder: error,
                softBg: lightenColor(error, 85),
            },
            warning: {
                plainColor: warning,
                softBg: lightenColor(warning, 85),
            },
        },
        textPrimary: "#1a1a1a",
        textSecondary: "#6b7280",
        textColor: "#1a1a1a",
        textDisabled: "#9ca3af",
        surface: card,
        onSurface: "#1a1a1a",
        surfaceVariant: "#f5f5f5",
        surfaceTintDim: "#6b7280",
        surfaceInverse: "#1a1a1a",
        outlineEnabledBorder: "#e5e7eb",
        secondaryContainer: "#f3f4f6",
    };

    // Build dark color scheme (inverted colors)
    const darkColorScheme = {
        palette: {
            primary: {
                plainColor: primary,
                outlinedBorder: primary,
                onPrimary: primaryForeground,
                primaryContainer: primary,
            },
            success: {
                plainColor: success,
                outlinedBorder: success,
                softBg: darkenColor(success, 70),
            },
            danger: {
                plainColor: error,
                outlinedBorder: error,
                softBg: darkenColor(error, 70),
            },
            warning: {
                plainColor: warning,
                softBg: darkenColor(warning, 70),
            },
        },
        textPrimary: "#FAFAFA",
        textSecondary: "#FAFAFA",
        textColor: "#FAFAFA",
        textDisabled: "#9E9E9E",
        surface: card,
        onSurface: "#212121",
        surfaceVariant: "#616161",
        surfaceTintDim: "#FAFAFA",
        surfaceInverse: "#FAFAFA",
        outlineEnabledBorder: "#757575",
        secondaryContainer: "#757575",
    };

    const radiusScale = mapRadiusScale(radius);

    return {
        mode: "dark",
        colorSchemes: {
            light: lightColorScheme,
            dark: darkColorScheme,
        },
        customBreakpoints: {
            values: {
                xs: 0,
                sm: 444,
                md: 600,
                lg: 900,
                xl: 1200,
                xxl: 1536,
            },
        },
        spacingScale: {
            "3xs": "2px",
            "2xs": "4px",
            xs: "8px",
            sm: "12px",
            md: "16px",
            lg: "24px",
            xl: "32px",
            "2xl": "40px",
            "3xl": "48px",
        },
        customFontFamily: {
            light: {
                primary: fontFamilyWithFallback,
                secondary: fontFamilyWithFallback,
            },
            dark: {
                primary: fontFamilyWithFallback,
                secondary: fontFamilyWithFallback,
            },
        },
        customRadius: {
            light: radiusScale,
            dark: radiusScale,
        },
        border: {
            light: { size: "1px" },
            dark: { size: "1px" },
        },
        imageBackgroundGradient: hasBackgroundImage
            ? {
                  dark: {
                      standard: "none",
                      split: "none",
                      overlay: "none",
                  },
              }
            : {
                  dark: {
                      standard: "linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, rgba(18, 18, 18, 0.8) 100%)",
                      split: "linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, rgba(18, 18, 18, 0.8) 100%)",
                      overlay: "linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, rgba(18, 18, 18, 0.8) 100%)",
                  },
              },
    };
}
